import { z } from "zod";

/**
 * GiftPass online orders (0370, spec docs/specs/giftpass-online-orders.md).
 *
 * The order form sends NO price and NO pass count: the server prices the order
 * from the package (`create_giftpass_order`). Unknown keys are stripped, so an
 * `amount` or `price` a browser adds never reaches the server's logic.
 */
const giftpassOrderFields = z.object({
  packageId: z.string().uuid(),
  quantity: z.number().int().min(1, "Order at least 1."),
  buyerName: z.string().trim().min(2, "Enter your name.").max(120),
  buyerPhone: z
    .string()
    .trim()
    .regex(/^(\+?63|0)9\d{9}$/, "Enter a mobile number like 09171234567."),
  buyerEmail: z.string().trim().toLowerCase().email("Enter a valid email.").max(254),
  referralId: z.string().uuid().nullable().default(null),
  physical: z.boolean().default(false),
  deliveryAddress: z.string().trim().max(500).nullable().default(null),
  clientRequestId: z.string().uuid(),
});

const needsAddress = (v: { physical: boolean; deliveryAddress: string | null }) =>
  !v.physical || (v.deliveryAddress ?? "").length >= 5;
const addressIssue = {
  message: "Enter the delivery address for the cards.",
  path: ["deliveryAddress"],
};

export const createGiftpassOrderSchema = giftpassOrderFields.refine(needsAddress, addressIssue);

export type CreateGiftpassOrderInput = z.input<typeof createGiftpassOrderSchema>;
export type CreateGiftpassOrder = z.output<typeof createGiftpassOrderSchema>;

/** Superadmin package edit (0370 `upsert_giftpass_package`). Audited server-side. */
export const upsertGiftpassPackageSchema = z.object({
  name: z.string().trim().min(1).max(80),
  price: z.number().positive().multipleOf(0.01),
  passes: z.number().int().min(1),
  freePasses: z.number().int().min(0),
  active: z.boolean(),
  /** 0373 — left out of the public catalog, still orderable by its link. */
  hidden: z.boolean().default(false),
  sortOrder: z.number().int().default(0),
});

export type UpsertGiftpassPackageInput = z.input<typeof upsertGiftpassPackageSchema>;

/** Superadmin Referral list edit (0370 `upsert_giftpass_referral`). Audited server-side. */
export const upsertGiftpassReferralSchema = z.object({
  name: z.string().trim().min(1).max(80),
  active: z.boolean(),
  sortOrder: z.number().int().default(0),
});

export type UpsertGiftpassReferralInput = z.input<typeof upsertGiftpassReferralSchema>;

/** How a manual order was paid (0373). */
export const GIFTPASS_HAND_PAYMENT_METHODS = ["bank_transfer", "cash", "ewallet", "other"] as const;
export type GiftpassHandPaymentMethod = (typeof GIFTPASS_HAND_PAYMENT_METHODS)[number];

/** Methods that need a proof of payment attached (Leon, 2026-10-26; 0375). */
export const GIFTPASS_PROOF_REQUIRED: readonly GiftpassHandPaymentMethod[] = [
  "bank_transfer",
  "ewallet",
];

/** A file in the private giftpass-payment-proofs bucket (0375). */
const proofPath = z
  .string()
  .regex(/^[0-9a-f-]{36}\/[0-9a-f-]{36}\.(jpg|png|webp|pdf)$/, "Attach the proof again.")
  .nullable()
  .default(null);

const needsProof = (v: { method?: GiftpassHandPaymentMethod | null; proofPath: string | null }) =>
  !v.method || !GIFTPASS_PROOF_REQUIRED.includes(v.method) || v.proofPath !== null;
const proofIssue = { message: "Attach the proof of payment.", path: ["proofPath"] };

/**
 * Superadmin manual order (0373, 0375 `create_manual_giftpass_order`): walk-in,
 * corporate, or above the QR Ph cap. Priced from the package like an online
 * order. Entered `paid` (method, reference, proof) its passes are created at
 * once; otherwise it waits as `awaiting_transfer`.
 */
export const createManualGiftpassOrderSchema = giftpassOrderFields
  .extend({
    paid: z.boolean().default(true),
    method: z.enum(GIFTPASS_HAND_PAYMENT_METHODS).nullable().default(null),
    reference: z.string().trim().max(120).nullable().default(null),
    proofPath,
  })
  .refine(needsAddress, addressIssue)
  .refine((v) => !v.paid || v.method !== null, {
    message: "Choose how it was paid.",
    path: ["method"],
  })
  .refine((v) => !v.paid || (v.reference ?? "").length > 0, {
    message: "Enter the payment reference.",
    path: ["reference"],
  })
  .refine((v) => !v.paid || needsProof(v), proofIssue);
export type CreateManualGiftpassOrderInput = z.input<typeof createManualGiftpassOrderSchema>;
export type CreateManualGiftpassOrder = z.output<typeof createManualGiftpassOrderSchema>;

/** Superadmin "Mark as paid" on a manual order (0373, 0375). Creates its passes. */
export const markGiftpassOrderPaidSchema = z
  .object({
    method: z.enum(GIFTPASS_HAND_PAYMENT_METHODS),
    reference: z.string().trim().min(1, "Enter the payment reference.").max(120),
    proofPath,
  })
  .refine(needsProof, proofIssue);
export type MarkGiftpassOrderPaidInput = z.input<typeof markGiftpassOrderPaidSchema>;

/** The print queue (0373): one step at a time, forward or back. */
export const GIFTPASS_PRINT_STATUSES = ["to_print", "printed", "delivered"] as const;
export type GiftpassPrintStatus = (typeof GIFTPASS_PRINT_STATUSES)[number];

export const setGiftpassPrintStatusSchema = z.object({
  printStatus: z.enum(GIFTPASS_PRINT_STATUSES),
});
export type SetGiftpassPrintStatusInput = z.input<typeof setGiftpassPrintStatusSchema>;

/** Public catalog for the order site: active packages and Referral names only. */
export interface GiftpassCatalog {
  packages: {
    id: string;
    name: string;
    price: number;
    passes: number;
    freePasses: number;
  }[];
  referrals: { id: string; name: string }[];
}

/** What the public order endpoints return — never the buyer's contact details. */
export interface GiftpassOrderPublic {
  orderId: string;
  orderCode: string;
  status: "pending_payment" | "awaiting_transfer" | "paid" | "expired" | "failed" | "cancelled";
  packageName: string;
  quantity: number;
  totalPasses: number;
  amount: number;
}

export interface GiftpassOrderCheckout extends GiftpassOrderPublic {
  qr: { imageUrl: string; expiresAt: string } | null;
}
